/**
 * Server policy: who this organisation is, who may join it, what each role may
 * do, and how long messages live.
 *
 * Saving is explicit. Every control writes to a local draft and nothing reaches
 * the server until Save is pressed, because these settings change what everyone
 * on the server may do — a permission that flipped because a select was tabbed
 * past is not a permission anybody decided to change. The draft is compared
 * against the loaded settings to work out what actually changed, so the audit
 * trail records the edit rather than a rewrite of the whole policy.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent, DragEvent as ReactDragEvent, FormEvent, ReactElement } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/api/auth'
import { api, isApiError } from '@/api/client'
import { queryKeys, useAdminSettings, useAdminUsers, useUpdateSettings } from '@/api/queries'
import type {
  FederationMode,
  FieldErrors,
  LogoUploadSession,
  PermissionAction,
  PermissionRole,
  RegistrationMode,
  ServerPermissions,
  ServerSettings,
  TransferOwnershipRequest,
  UpdateSettingsRequest,
  User,
} from '@/api/types'
import {
  Button,
  Checkbox,
  Modal,
  Select,
  SpaceLogo,
  Spinner,
  TextArea,
  TextField,
  useToast,
} from '@/components/ui'
function formatAdminDay(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' }).format(date)
}

import {
  AdminPage,
  Card,
  ErrorNotice,
  Fact,
  Facts,
  Notice,
  cx,
  describeError,
  useUnsavedChanges,
} from './AdminLayout'
import styles from './admin.module.css'

// ── Vocabulary ──────────────────────────────────────────────────────────────

const REGISTRATION_MODES: ReadonlyArray<{
  value: RegistrationMode
  label: string
  hint: string
}> = [
  {
    value: 'invite_only',
    label: 'Invitation only',
    hint: 'Nobody can create an account without an invitation from an administrator.',
  },
  {
    value: 'email_domains',
    label: 'Anyone with an allowed email address',
    hint: 'Anybody whose email address ends in one of the domains below may sign themselves up.',
  },
  {
    value: 'open',
    label: 'Open to anyone',
    hint: 'Anybody who can reach this server may create an account. Suitable for a closed network only.',
  },
]

const FEDERATION_MODES: ReadonlyArray<{ value: FederationMode; label: string; hint: string }> = [
  {
    value: 'approved_peers',
    label: 'Approved servers only',
    hint: 'Another server can talk to this one once an administrator here has approved its key.',
  },
  {
    value: 'disabled',
    label: 'No servers',
    hint: 'Federated requests are refused even while federation is switched on.',
  },
]

const PERMISSION_ACTIONS: ReadonlyArray<{
  key: PermissionAction
  label: string
  hint: string
}> = [
  {
    key: 'create_channels',
    label: 'Create channels',
    hint: 'Open a new channel on this server',
  },
  {
    key: 'create_group_chats',
    label: 'Start group conversations',
    hint: 'Open a private conversation with several people',
  },
  {
    key: 'invite_users',
    label: 'Invite people in',
    hint: 'Send an invitation to somebody without an account',
  },
  {
    key: 'create_webhooks',
    label: 'Create webhooks',
    hint: 'Let an outside system post into a channel',
  },
  { key: 'manage_widgets', label: 'Manage embeds and widgets', hint: 'Create, edit and remove widgets in channels you manage' },
  { key: 'upload_files', label: 'Upload files', hint: 'Attach files to messages' },
]

const PERMISSION_ROLES: ReadonlyArray<{ key: PermissionRole; label: string }> = [
  { key: 'owner', label: 'Owner' },
  { key: 'admin', label: 'Admin' },
  { key: 'moderator', label: 'Moderator' },
  { key: 'member', label: 'Member' },
]

function toRegistrationMode(value: string): RegistrationMode {
  return value === 'open' || value === 'email_domains' ? value : 'invite_only'
}

function toFederationMode(value: string): FederationMode {
  return value === 'approved_peers' ? 'approved_peers' : 'disabled'
}

// ── The draft ───────────────────────────────────────────────────────────────

interface Draft {
  display_name: string
  description: string
  registration_mode: RegistrationMode
  allowed_email_domains: string
  federation_enabled: boolean
  federation_mode: FederationMode
  permissions: ServerPermissions
  message_editing_enabled: boolean
  message_deleting_enabled: boolean
  message_edit_window_seconds: string
  retention_days: string
  calls_enabled: boolean
  push_enabled: boolean
  push_previews_enabled: boolean
  email_provider: 'smtp' | 'brevo' | 'sendgrid' | 'mailgun' | 'postmark' | 'none'
  email_from: string
  email_api_key: string
  require_mfa: boolean
  update_check_enabled: boolean
}

function toDraft(settings: ServerSettings): Draft {
  return {
    display_name: settings.display_name ?? '',
    description: settings.description ?? '',
    registration_mode: settings.registration_mode,
    allowed_email_domains: settings.allowed_email_domains.join('\n'),
    federation_enabled: settings.federation_enabled,
    federation_mode: settings.federation_mode,
    permissions: { ...settings.permissions, manage_widgets: settings.permissions.manage_widgets ?? 'moderator' },
    message_editing_enabled: settings.message_editing_enabled,
    message_deleting_enabled: settings.message_deleting_enabled,
    message_edit_window_seconds:
      settings.message_edit_window_seconds === null
        ? ''
        : String(settings.message_edit_window_seconds),
    retention_days: settings.retention_days === null ? '' : String(settings.retention_days),
    calls_enabled: settings.calls_enabled !== false,
    push_enabled: settings.push_enabled,
    push_previews_enabled: settings.push_previews_enabled === true,
    email_provider: settings.email_provider ?? 'smtp',
    email_from: settings.email_from ?? '',
    email_api_key: '',
    require_mfa: settings.require_mfa === true,
    update_check_enabled: settings.update_check_enabled !== false,
  }
}

/** One domain per line or comma; lower-cased and de-duplicated. */
function parseDomains(value: string): string[] {
  const seen = new Set<string>()

  for (const candidate of value.split(/[\s,]+/u)) {
    const domain = candidate.trim().toLowerCase()
    if (domain !== '') seen.add(domain)
  }

  return [...seen]
}

/** `null` for "no limit", `undefined` for text that is not a whole count. */
function parseCount(value: string): number | null | undefined {
  const trimmed = value.trim()
  if (trimmed === '') return null
  if (!/^\d+$/u.test(trimmed)) return undefined

  const parsed = Number.parseInt(trimmed, 10)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined
}

/**
 * A stable rendering of everything the form can change, used only to compare
 * two drafts. Keys are written in a fixed order so the comparison never depends
 * on how the objects were built.
 */
function serialise(draft: Draft): string {
  return JSON.stringify([
    draft.display_name.trim(),
    draft.description.trim(),
    draft.registration_mode,
    parseDomains(draft.allowed_email_domains),
    draft.federation_enabled,
    draft.federation_mode,
    PERMISSION_ACTIONS.map((action) => draft.permissions[action.key]),
    draft.message_editing_enabled,
    draft.message_deleting_enabled,
    draft.message_edit_window_seconds.trim(),
    draft.retention_days.trim(),
    draft.calls_enabled,
    draft.push_enabled,
    draft.push_previews_enabled,
    draft.email_provider,
    draft.email_from.trim(),
    draft.email_api_key,
    draft.require_mfa,
    draft.update_check_enabled,
  ])
}

function buildChanges(
  draft: Draft,
  baseline: Draft,
  editWindow: number | null,
  retention: number | null,
): UpdateSettingsRequest {
  const changes: UpdateSettingsRequest = {}

  if (draft.display_name.trim() !== baseline.display_name.trim()) {
    changes.display_name = draft.display_name.trim()
  }

  if (draft.description.trim() !== baseline.description.trim()) {
    const description = draft.description.trim()
    changes.description = description === '' ? null : description
  }

  if (draft.registration_mode !== baseline.registration_mode) {
    changes.registration_mode = draft.registration_mode
  }

  const domains = parseDomains(draft.allowed_email_domains)
  if (domains.join(',') !== parseDomains(baseline.allowed_email_domains).join(',')) {
    changes.allowed_email_domains = domains
  }

  if (draft.federation_enabled !== baseline.federation_enabled) {
    changes.federation_enabled = draft.federation_enabled
  }

  if (draft.federation_mode !== baseline.federation_mode) {
    changes.federation_mode = draft.federation_mode
  }

  const permissions: Partial<ServerPermissions> = {}
  let permissionsChanged = false
  for (const action of PERMISSION_ACTIONS) {
    if (draft.permissions[action.key] !== baseline.permissions[action.key]) {
      permissions[action.key] = draft.permissions[action.key]
      permissionsChanged = true
    }
  }
  if (permissionsChanged) changes.permissions = permissions

  if (draft.message_editing_enabled !== baseline.message_editing_enabled) {
    changes.message_editing_enabled = draft.message_editing_enabled
  }

  if (draft.message_deleting_enabled !== baseline.message_deleting_enabled) {
    changes.message_deleting_enabled = draft.message_deleting_enabled
  }

  if (draft.message_edit_window_seconds.trim() !== baseline.message_edit_window_seconds.trim()) {
    changes.message_edit_window_seconds = editWindow
  }

  if (draft.retention_days.trim() !== baseline.retention_days.trim()) {
    changes.retention_days = retention
  }

  if (draft.calls_enabled !== baseline.calls_enabled) {
    changes.calls_enabled = draft.calls_enabled
  }

  if (draft.push_enabled !== baseline.push_enabled) {
    changes.push_enabled = draft.push_enabled
  }

  if (draft.push_previews_enabled !== baseline.push_previews_enabled) {
    changes.push_previews_enabled = draft.push_previews_enabled
  }

  if (draft.require_mfa !== baseline.require_mfa) {
    changes.require_mfa = draft.require_mfa
  }

  if (draft.update_check_enabled !== baseline.update_check_enabled) {
    changes.update_check_enabled = draft.update_check_enabled
  }

  if (draft.email_provider !== baseline.email_provider) changes.email_provider = draft.email_provider
  if (draft.email_from.trim() !== baseline.email_from.trim()) {
    changes.email_from = draft.email_from.trim() === '' ? null : draft.email_from.trim()
  }
  if (draft.email_api_key.trim() !== '') changes.email_api_key = draft.email_api_key.trim()

  return changes
}

// ── The Space logo ──────────────────────────────────────────────────────────

/** What `Relaii.Orgs.Logo` accepts. Checked here so a doomed upload never starts. */
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif']
const LOGO_MAX_BYTES = 5 * 1024 * 1024

const LOGO_HINT =
  'A square image, at least 256×256 px. PNG, JPEG, GIF, WebP or AVIF up to 5 MB. ' +
  "Shown in the sidebar, at sign-in and in the apps' Space switcher. Without one, clients show the relaii mark."

/** The first thing wrong with a chosen file, in the words the field will use. */
function logoProblemFor(file: File): string | null {
  if (!LOGO_TYPES.includes(file.type)) return 'Choose a PNG, JPEG, GIF, WebP or AVIF image.'
  if (file.size > LOGO_MAX_BYTES) return 'The logo must be 5 MB or smaller.'
  if (file.size === 0) return 'Choose an image that is not empty.'
  return null
}

function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`
}

function describeSeconds(seconds: number): string {
  if (seconds % 86_400 === 0) return plural(seconds / 86_400, 'day', 'days')
  if (seconds % 3600 === 0) return plural(seconds / 3600, 'hour', 'hours')
  if (seconds % 60 === 0) return plural(seconds / 60, 'minute', 'minutes')
  return plural(seconds, 'second', 'seconds')
}

// ── The page ────────────────────────────────────────────────────────────────

export function SettingsPage(): ReactElement {
  const auth = useAuth()
  const query = useAdminSettings()
  const update = useUpdateSettings()
  const queryClient = useQueryClient()
  const toast = useToast()

  const settings = query.data
  const baseline = useMemo(() => (settings === undefined ? null : toDraft(settings)), [settings])

  const [draft, setDraft] = useState<Draft | null>(null)
  const [loadedVersion, setLoadedVersion] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  // The logo is uploaded the moment it is chosen, so it carries its own busy
  // state rather than riding on the draft's Save row. Which of the two actions
  // is running decides where the spinner goes.
  const [logoBusy, setLogoBusy] = useState<'upload' | 'remove' | null>(null)
  const [logoProgress, setLogoProgress] = useState<number | null>(null)
  const [logoProblem, setLogoProblem] = useState<string | null>(null)
  const [logoDropTarget, setLogoDropTarget] = useState(false)

  // Re-seeded only when the server's copy actually moved on. A background
  // refetch that returns the same settings must not wipe out an edit in
  // progress, and a successful save must reset the form to what was saved.
  useEffect(() => {
    if (settings === undefined || loadedVersion === settings.updated_at) return

    setLoadedVersion(settings.updated_at)
    setDraft(toDraft(settings))
    setFieldErrors({})
  }, [settings, loadedVersion])

  const dirty =
    draft !== null && baseline !== null && serialise(draft) !== serialise(baseline)

  useUnsavedChanges(dirty)

  if (query.isPending) {
    return (
      <AdminPage title="Server">
        <Spinner size="lg" label="Loading server settings" />
      </AdminPage>
    )
  }

  if (query.isError) {
    return (
      <AdminPage title="Server">
        <ErrorNotice error={query.error} title="The server settings could not be loaded">
          <Button onClick={() => void query.refetch()}>Try again</Button>
        </ErrorNotice>
      </AdminPage>
    )
  }

  if (draft === null || baseline === null || settings === undefined) {
    return (
      <AdminPage title="Server">
        <Spinner size="lg" label="Preparing the form" />
      </AdminPage>
    )
  }

  const patch = (changes: Partial<Draft>): void => {
    setDraft((current) => (current === null ? current : { ...current, ...changes }))
  }

  const setPermission = (action: PermissionAction, role: PermissionRole): void => {
    setDraft((current) => {
      if (current === null) return current
      const permissions: ServerPermissions = { ...current.permissions }
      permissions[action] = role
      return { ...current, permissions }
    })
  }

  const errorFor = (field: string): string | null => fieldErrors[field]?.[0] ?? null

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    const editWindow = parseCount(draft.message_edit_window_seconds)
    const retention = parseCount(draft.retention_days)
    const problems: FieldErrors = {}

    if (editWindow === undefined) {
      problems['message_edit_window_seconds'] = [
        'Enter a whole number of seconds, or leave it blank for no limit.',
      ]
    }
    if (retention === undefined) {
      problems['retention_days'] = [
        'Enter a whole number of days, or leave it blank to keep messages forever.',
      ]
    }

    if (Object.keys(problems).length > 0) {
      setFieldErrors(problems)
      return
    }

    const changes = buildChanges(
      draft,
      baseline,
      editWindow ?? null,
      retention ?? null,
    )

    if (Object.keys(changes).length === 0) return

    setFieldErrors({})
    update.mutate(changes, {
      onSuccess: () => {
      toast.success('Server settings saved.')
      },
      onError: (error: unknown) => {
        if (isApiError(error) && Object.keys(error.fieldErrors).length > 0) {
          setFieldErrors(error.fieldErrors)
        }
        toast.error(describeError(error, 'The settings could not be saved.'))
      },
    })
  }

  const discard = (): void => {
    setDraft(toDraft(settings))
    setFieldErrors({})
  }

  /**
   * Adopts a settings object the server has just returned.
   *
   * The logo is changed outside the draft, so a half-typed display name has to
   * survive it. Marking the version as already loaded is what stops the
   * re-seeding effect from firing on the newer `updated_at` and wiping the
   * edit in progress.
   */
  const adoptSettings = (saved: ServerSettings): void => {
    queryClient.setQueryData<ServerSettings>(queryKeys.admin.settings(), saved)
    setLoadedVersion(saved.updated_at)
    void queryClient.invalidateQueries({ queryKey: queryKeys.server() })
  }

  const uploadLogo = async (file: File): Promise<void> => {
    const problem = logoProblemFor(file)
    if (problem !== null) {
      setLogoProblem(problem)
      return
    }

    setLogoProblem(null)
    setLogoBusy('upload')
    setLogoProgress(0)

    try {
      const reserved = await api.post<LogoUploadSession>('/admin/settings/logo/uploads', {
        filename: file.name,
        mime_type: file.type,
        byte_size: file.size,
      })

      await api.upload(reserved.upload.url, file, reserved.upload.headers, setLogoProgress)

      const saved = await api.post<ServerSettings>(
        `/admin/settings/logo/uploads/${reserved.logo_upload_id}/finalize`,
        {},
      )

      adoptSettings(saved)
      toast.success('Logo updated.')
    } catch (error: unknown) {
      const message = describeError(error, 'The logo could not be uploaded.')
      setLogoProblem(message)
      toast.error(message)
    } finally {
      setLogoBusy(null)
      setLogoProgress(null)
    }
  }

  const chooseLogo = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0] ?? null
    // Allow choosing the same file again after correcting a validation error.
    event.target.value = ''
    if (file !== null) void uploadLogo(file)
  }

  const droppedFiles = (event: ReactDragEvent<HTMLLabelElement>): boolean =>
    Array.from(event.dataTransfer.types).includes('Files')

  const onLogoDragOver = (event: ReactDragEvent<HTMLLabelElement>): void => {
    if (logoBusy !== null || !droppedFiles(event)) return
    event.preventDefault()
    setLogoDropTarget(true)
  }

  const onLogoDrop = (event: ReactDragEvent<HTMLLabelElement>): void => {
    event.preventDefault()
    setLogoDropTarget(false)
    if (logoBusy !== null) return
    const file = event.dataTransfer.files[0] ?? null
    if (file !== null) void uploadLogo(file)
  }

  const removeLogo = async (): Promise<void> => {
    setLogoBusy('remove')
    setLogoProblem(null)

    try {
      adoptSettings(await api.del<ServerSettings>('/admin/settings/logo'))
      toast.success('Logo removed.')
    } catch (error: unknown) {
      const message = describeError(error, 'The logo could not be removed.')
      setLogoProblem(message)
      toast.error(message)
    } finally {
      setLogoBusy(null)
    }
  }

  // The label follows the name being typed, so the preview and its alt text
  // describe the same Space the form is about to save.
  const spaceName = draft.display_name.trim() === '' ? settings.server_id : draft.display_name.trim()

  const registration = REGISTRATION_MODES.find(
    (mode) => mode.value === draft.registration_mode,
  )
  const federation = FEDERATION_MODES.find((mode) => mode.value === draft.federation_mode)
  const editWindowSeconds = parseCount(draft.message_edit_window_seconds)

  return (
    <AdminPage
      title="Server"
      lead="The identity of this organisation, who may join it, what each role may do, and how long messages are kept."
    >
      <form className={styles.stack} onSubmit={onSubmit} noValidate>
        <Card
          title="Organisation"
          lead="Shown on the sign-in screen and to anybody you federate with."
        >
          <div className={styles.stack}>
            <TextField
              label="Display name"
              labelExtra={`${draft.display_name.length}/120`}
              value={draft.display_name}
              onChange={(event) => patch({ display_name: event.target.value })}
              error={errorFor('display_name')}
              maxLength={120}
              autoComplete="organization"
            />
            <TextArea
              label="Description"
              labelExtra={`${draft.description.length}/2000`}
              value={draft.description}
              onChange={(event) => patch({ description: event.target.value })}
              error={errorFor('description')}
              rows={3}
              hint="One or two sentences. Optional."
            />

            <div className={styles.logoField}>
              <span className={styles.logoLabel}>Logo</span>
              <div className={styles.logoRow}>
                <SpaceLogo src={settings.logo_url} name={spaceName} size="xl" />
                <label
                  className={cx(
                    styles.logoDropzone,
                    logoDropTarget && styles.logoDropzoneActive,
                    logoBusy !== null && styles.logoDropzoneDisabled,
                  )}
                  onDragOver={onLogoDragOver}
                  onDragLeave={() => setLogoDropTarget(false)}
                  onDrop={onLogoDrop}
                >
                  <input
                    type="file"
                    aria-label="Space logo"
                    accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
                    onChange={chooseLogo}
                    disabled={logoBusy !== null}
                  />
                  <span className={styles.logoDropzoneTitle}>
                    {logoDropTarget ? 'Drop image to upload' : 'Upload logo…'}
                  </span>
                  {/* Standing rather than per-file: every logo is cropped square on the
                      way in, so saying so once is always true, and an upload that failed
                      leaves no claim behind that nothing was cropped. */}
                  <span className={styles.logoDropzoneHint}>
                    or drop one here. Cropped to a square on upload.
                  </span>
                </label>
                {settings.logo_url === null ? null : (
                  <Button
                    onClick={() => void removeLogo()}
                    disabled={logoBusy === 'upload'}
                    loading={logoBusy === 'remove'}
                  >
                    Remove
                  </Button>
                )}
              </div>

              {logoProgress === null ? null : (
                <div
                  className={styles.logoProgress}
                  role="progressbar"
                    aria-label="Logo upload"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(logoProgress * 100)}
                >
                  <span style={{ width: `${Math.round(logoProgress * 100)}%` }} />
                </div>
              )}

              <small className={styles.hint}>{LOGO_HINT}</small>
              {logoProblem === null ? null : (
                <small className={styles.logoError} role="alert">
                  {logoProblem}
                </small>
              )}
            </div>

            <Facts>
              <Fact term="Server address">
                <span className={styles.mono}>{settings.server_id}</span>
              </Fact>
              <Fact term="Set up">
                {settings.setup_completed_at === null
                  ? 'Not finished'
                  : formatAdminDay(settings.setup_completed_at)}
              </Fact>
              <Fact term="Last changed">{formatAdminDay(settings.updated_at)}</Fact>
            </Facts>
          </div>
        </Card>

        <Card title="Who may join" lead="How an account on this server comes into being.">
          <div className={styles.stack}>
            <Select
              label="Registration"
              value={draft.registration_mode}
              onChange={(event) =>
                patch({ registration_mode: toRegistrationMode(event.target.value) })
              }
              options={REGISTRATION_MODES.map((mode) => ({
                value: mode.value,
                label: mode.label,
              }))}
              hint={registration?.hint}
              error={errorFor('registration_mode')}
            />

            {draft.registration_mode === 'email_domains' ? (
              <TextArea
                label="Allowed email domains"
                value={draft.allowed_email_domains}
                onChange={(event) => patch({ allowed_email_domains: event.target.value })}
                rows={3}
                hint="One domain per line, for example oclin.no. Sub-domains are not included."
                error={errorFor('allowed_email_domains')}
                spellCheck={false}
              />
            ) : null}
          </div>
        </Card>

        <Card
          title="Federation"
          lead="Whether this server may exchange channels and messages with other relaii servers."
        >
          <div className={styles.stack}>
            <Checkbox
              label="Allow federation"
              checked={draft.federation_enabled}
              onChange={(event) => patch({ federation_enabled: event.target.checked })}
              hint="With this off, every federated request is refused and the Federation page is closed."
            />
            <Select
              label="Which servers"
              value={draft.federation_mode}
              onChange={(event) => patch({ federation_mode: toFederationMode(event.target.value) })}
              options={FEDERATION_MODES.map((mode) => ({
                value: mode.value,
                label: mode.label,
              }))}
              hint={federation?.hint}
              disabled={!draft.federation_enabled}
              error={errorFor('federation_mode')}
            />
          </div>
        </Card>

        <Card
          title="Permissions"
          lead="The lowest role that may take each action. A role may always do everything the roles below it may do."
        >
          <div className={styles.tableWrap}>
            <table className={cx(styles.table, styles.matrix)}>
              <thead>
                <tr>
                  <th scope="col">Action</th>
                  {PERMISSION_ROLES.map((role) => (
                    <th scope="col" key={role.key}>
                      {role.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_ACTIONS.map((action) => (
                  <tr key={action.key}>
                    <th scope="row">
                      {action.label}
                      <span className={styles.hint}>{action.hint}</span>
                    </th>
                    {PERMISSION_ROLES.map((role) => (
                      <td key={role.key}>
                        <label className={styles.radioCell}>
                          <input
                            type="radio"
                            name={`permission-${action.key}`}
                            value={role.key}
                            checked={draft.permissions[action.key] === role.key}
                            onChange={() => setPermission(action.key, role.key)}
                          />
                          <span className="sr-only">{`${action.label}: ${role.label} and above`}</span>
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card
          title="Messages"
          lead="What people may do to a message after they have sent it, and how long the server keeps it."
        >
          <div className={styles.stack}>
            <Checkbox
              label="People may edit their own messages"
              checked={draft.message_editing_enabled}
              onChange={(event) => patch({ message_editing_enabled: event.target.checked })}
            />
            <TextField
              label="Redigeringsvindu (sekunder)"
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={draft.message_edit_window_seconds}
              onChange={(event) => patch({ message_edit_window_seconds: event.target.value })}
              disabled={!draft.message_editing_enabled}
              error={errorFor('message_edit_window_seconds')}
              hint={
                typeof editWindowSeconds === 'number'
                  ? `An edit is possible for ${describeSeconds(editWindowSeconds)} after sending.`
                  : 'Leave blank to let people edit their messages at any time.'
              }
            />
            <Checkbox
              label="People may delete their own messages"
              checked={draft.message_deleting_enabled}
              onChange={(event) => patch({ message_deleting_enabled: event.target.checked })}
              hint="A deleted message leaves a placeholder so the conversation keeps its shape."
            />
            <TextField
              label="Retention (days)"
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={draft.retention_days}
              onChange={(event) => patch({ retention_days: event.target.value })}
              error={errorFor('retention_days')}
              hint="Messages older than this are removed permanently. Leave blank to keep everything."
            />
          </div>
        </Card>

        <Card
          title="Voice and video"
          lead="Voice and video calls use WebRTC between clients. The relaii server relays signalling and mints TURN credentials; media stays peer-to-peer unless NAT requires relay through coturn."
        >
          <div className={styles.stack}>
            <Checkbox
              label="Allow voice and video calls"
              checked={draft.calls_enabled}
              onChange={(event) => patch({ calls_enabled: event.target.checked })}
              hint="When off, clients hide call controls and new calls are refused. TURN can keep running in Docker."
            />
          </div>
        </Card>

        <Card
          title="Updates"
          lead="relaii never installs an update by itself. It can look up the latest stable version, so the Status page can tell you when one is out."
        >
          <div className={styles.stack}>
            <Checkbox
              label="Check releases.relaii.app for a new version once a day"
              checked={draft.update_check_enabled}
              onChange={(event) => patch({ update_check_enabled: event.target.checked })}
              hint="The request carries this server's IP address and its relaii version, and nothing else. releases.relaii.app counts installations from it without keeping the address. When off, the Status page can still check when you ask."
            />
          </div>
        </Card>

        <Card
          title="Notifications"
          lead="Choose how this server delivers push and email notifications. The selected email provider is used for invitations, password resets and other transactional mail."
        >
          <div className={styles.stack}>
            <Facts>
              <Fact term="Apple Push">{draft.push_enabled ? 'Enabled' : 'Disabled'}</Fact>
              <Fact term="Android Push">{draft.push_enabled ? 'Enabled' : 'Disabled'}</Fact>
            </Facts>
            <Checkbox
              label="Enable push notifications"
              checked={draft.push_enabled}
              onChange={(event) => patch({ push_enabled: event.target.checked })}
              hint="The server registers itself with the gateway and stores only opaque device ids. Message text never leaves this installation."
            />
            <Checkbox
              label="Show the sender and message text in push notifications"
              checked={draft.push_previews_enabled}
              onChange={(event) => patch({ push_previews_enabled: event.target.checked })}
              hint="One choice for everyone in this Space. The text is encrypted for the phone or Mac that receives it; the gateway, Apple and Google only ever see the ciphertext. When off, a notification shows the Space's name and a generic line."
            />
            {settings.push_registered_at !== null ? (
              <Facts>
                <Fact term="Registered with the gateway">
                  {formatAdminDay(settings.push_registered_at)}
                </Fact>
              </Facts>
            ) : draft.push_enabled ? (
              <p className={styles.small}>
                Registration happens after you save. A failure there does not turn chat off.
              </p>
            ) : null}
            <Select
              label="Default email provider"
              value={draft.email_provider}
              onChange={(event) =>
                patch({ email_provider: event.target.value as Draft['email_provider'] })
              }
              options={[
                { value: 'brevo', label: 'Brevo' },
                { value: 'sendgrid', label: 'SendGrid' },
                { value: 'mailgun', label: 'Mailgun' },
                { value: 'postmark', label: 'Postmark' },
                { value: 'none', label: 'Disabled' },
              ]}
              hint="SMTP uses the server environment. API-based providers use the key below."
            />
            {draft.email_provider !== 'none' ? (
              <>
                <TextField
                  label="Sender email"
                  type="email"
                  value={draft.email_from}
                  maxLength={254}
                  labelExtra={`${draft.email_from.length}/254`}
                  onChange={(event) => patch({ email_from: event.target.value })}
                  hint="The address recipients will see on outgoing email."
                />
                <TextField
                  label={`${draft.email_provider.charAt(0).toUpperCase()}${draft.email_provider.slice(1)} API key`}
                  type="password"
                  value={draft.email_api_key}
                  onChange={(event) => patch({ email_api_key: event.target.value })}
                  placeholder={settings.email_api_key_configured ? 'Configured — enter a new key to replace it' : 'Paste API key'}
                  hint={settings.email_api_key_configured ? 'A key is already stored securely.' : 'Stored encrypted on the server.'}
                />
              </>
            ) : null}
          </div>
        </Card>

        <Card
          title="Sign-in"
          lead="Whether a password alone is enough to open a session on this server."
        >
          <div className={styles.stack}>
            <Checkbox
              label="Require two-factor authentication"
              checked={draft.require_mfa}
              onChange={(event) => patch({ require_mfa: event.target.checked })}
              hint="Everyone without an authenticator will be asked to set one up before they can read messages. An administrator can clear a locked account's factor from People."
            />
          </div>
        </Card>

        {dirty ? (
          <div className={styles.saveBar} role="region" aria-label="Unsaved changes">
            <span>Changes on this page have not been saved.</span>
            <div className={styles.saveBarActions}>
              <Button onClick={discard} disabled={update.isPending}>
                Discard
              </Button>
              <Button type="submit" variant="primary" loading={update.isPending}>
                Save changes
              </Button>
            </div>
          </div>
        ) : null}
      </form>

      {auth.user !== null && auth.user.is_owner ? (
        <TransferOwnership ownerId={auth.user.id} />
      ) : null}
    </AdminPage>
  )
}

// ── Transfer of ownership ───────────────────────────────────────────────────

function TransferOwnership({ ownerId }: { ownerId: string }): ReactElement {
  const users = useAdminUsers()
  const queryClient = useQueryClient()
  const auth = useAuth()
  const toast = useToast()

  const [targetId, setTargetId] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [typed, setTyped] = useState('')
  const [password, setPassword] = useState('')
  const confirmField = useRef<HTMLInputElement>(null)

  const candidates = useMemo(
    () =>
      (users.data ?? []).filter(
        (candidate) =>
          candidate.id !== ownerId &&
          candidate.is_local &&
          !candidate.is_bot &&
          candidate.status === 'active',
      ),
    [users.data, ownerId],
  )

  const target: User | undefined = candidates.find((candidate) => candidate.id === targetId)

  const transfer = useMutation({
    // The server asks the owner to prove it is them before the one thing an
    // owner cannot undo, so the password travels with the request.
    mutationFn: (body: TransferOwnershipRequest) =>
      api.post<User>('/admin/transfer-ownership', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.users.all() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all() })
      // This admin's own record changed too, so the held copy is now wrong.
      void auth.refresh()
    },
  })

  const close = (): void => {
    setConfirming(false)
    setTyped('')
    setPassword('')
  }

  const ready = target !== undefined && typed.trim() === target.username && password !== ''

  const confirm = (): void => {
    if (target === undefined || !ready) return

    transfer.mutate({ user_id: target.id, password }, {
      onSuccess: (promoted) => {
        close()
        setTargetId('')
        toast.success(`${promoted.username} is now the owner of this server.`)
      },
      onError: (error: unknown) => {
        toast.error(describeError(error, 'Ownership could not be transferred.'))
      },
    })
  }

  return (
    <Card
      danger
      title="Transfer ownership"
      lead="A server has exactly one owner. Handing it over makes somebody else the owner; you keep your administrator account."
    >
      <div className={styles.stack}>
        <Notice tone="warning">
          The owner is the only person who can transfer ownership and rotate this server&apos;s
          federation identity key. Once you hand it over you cannot take it back yourself.
        </Notice>

        {users.isError ? (
          <ErrorNotice error={users.error} title="The user list could not be loaded" />
        ) : (
          <div className={styles.row}>
            <Select
              className={styles.grow}
              label="New owner"
              value={targetId}
              placeholder={users.isPending ? 'Loading people…' : 'Choose a person'}
              onChange={(event) => setTargetId(event.target.value)}
              options={candidates.map((candidate) => ({
                value: candidate.id,
                label: `${candidate.display_name} (${candidate.username})`,
              }))}
              disabled={users.isPending || candidates.length === 0}
              hint={
                candidates.length === 0 && !users.isPending
                  ? 'There is no other active account on this server to hand it to.'
                  : 'Only active accounts on this server can be made owner.'
              }
            />
            <Button
              variant="danger"
              onClick={() => setConfirming(true)}
              disabled={target === undefined}
            >
              Transfer ownership…
            </Button>
          </div>
        )}
      </div>

      <Modal
        open={confirming && target !== undefined}
        onClose={close}
        title="Transfer ownership of this server"
        description="This takes effect immediately."
        size="sm"
        closeOnOverlayClick={false}
        initialFocusRef={confirmField}
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="danger"
              loading={transfer.isPending}
              disabled={!ready}
              onClick={confirm}
            >
              Transfer ownership
            </Button>
          </>
        }
      >
        {target === undefined ? null : (
          <div className={styles.stack}>
            <p>
              <strong>{target.display_name}</strong> becomes the owner of this server. You stay an
              administrator, but only they will be able to transfer ownership or rotate the
              federation identity key from then on.
            </p>
            <TextField
              ref={confirmField}
              label={`Type ${target.username} to confirm`}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <TextField
              label="Your password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              hint="Handing over the server is confirmed with your own password."
            />
          </div>
        )}
      </Modal>
    </Card>
  )
}
